import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import BreadCrumb from "../../../Header/SecondHeader";
import trans, { isRTL } from "../../../../utils";
import SubmissionHead from "./SubmissionParts/SubmissionHead";
import { formatDateString } from "../../../../JsHelpers/DateFormat";
import AntdTable from "../../../antd/Table/Table";
import { useGetAllformsSubmissionQuery } from "../../../../Redux/service/Forms/Form";
import { filtrationcontext } from "../../../../context/Filtercontext";
import Button from "../../../Helpers/Button/Button";
import Searchbar from "../../../Helpers/Searchbar/Searchbar";
import FormSubmissionPopup from "./FormSubmissionPopup";
import NearMissAddPopup from "./NearMissAddPopup";
import usePermission from "../../../../hooks/usePermission";
import { Tag, message } from "antd";

// Near Miss form ID comes from env (string -> number)
const NEAR_MISS_FORM_ID = Number(process.env.REACT_APP_NEAR_MISS_FORM_ID);

// Schema keys of the Near Miss fields shown as table columns. Keys are generated with the
// field and never change; titles are editable in the form builder, so matching on the key
// keeps these columns working after a rename (with the title kept as a fallback for rows
// submitted before the field carried its current key).
const FIELD_KEYS = {
  projectName: { key: "input-425d5878", title: "Project Name" },
  concernedDepartment: { key: "input-4d0d6d29", title: "Concerned Department" },
  centralDepartment: { key: "input-bcedc09b", title: "Central Department" },
};

const getFieldValue = (record, { key, title }) => {
  const fields = Array.isArray(record?.formData) ? record.formData : [];
  const field =
    fields.find((f) => f?.key === key) || fields.find((f) => f?.title === title);
  const value = field?.value;
  return value === undefined || value === null || value === "" ? "- - - - -" : value;
};

const handlePrint = async (formSubmissionId) => {
  try {
    const token = localStorage.getItem("token"); // adjust key if needed
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND2_URL}/v1/formSubmissions/${formSubmissionId}/pdf`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      }
    );

    if (!response.ok) throw new Error("Failed to fetch PDF");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(
      new Blob([blob], { type: "application/pdf" })
    );

    // Open in new tab (user can print/download from there)
    window.open(url, "_blank");
  } catch (error) {
    console.error("Print error:", error);
    message.error(trans("Failed to print form"));
  }
};

export default function NearMiss() {
  const [submittedData, setSubmittedData] = useState(null);
  const [view, setView] = useState(false);
  const [add, setAdd] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { create: canCreateNearMiss } = usePermission("NEAR_MISS");
  const {
    limit,
    setLimit,
    setSort: SetSortFilter,
    searchQuery,
    setSearchQuery,
  } = useContext(filtrationcontext);

  // Newest first: the list defaults to id-ascending server-side, which buries a report the
  // user just filed on the last page.
  const [sort, setSort] = useState({
    by: "submittedAt",
    order: "desc",
  });

  const [query, setQuery] = useState("");
  const handleSearch = (e) => {
    setQuery(e.target.value);
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Form ID is fixed for Near Miss, sourced from env
  const [filter] = useState({ formId: NEAR_MISS_FORM_ID, taskId: null });

  const buildQueryString = (filterObj) => {
    const query = Object.entries(filterObj)
      .filter(
        ([_, value]) =>
          value !== null &&
          value !== "" &&
          value !== undefined &&
          !Number.isNaN(value)
      )
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return query ? `&${query}` : "";
  };

  const queryString = buildQueryString(filter);
  let {
    data: AllForms,
    isLoading,
    refetch,
  } = useGetAllformsSubmissionQuery(
    {
      page: currentPage,
      limit: limit,
      search: searchQuery,
      sortby: sort.by,
      sortorder: sort.order,
      queryString: queryString,
    },
    {
      skip: !currentPage || !limit,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    return () => {
      SetSortFilter({ type: "", value: "" });
      setCurrentPage(1);
      setSearchQuery("");
    };
  }, [SetSortFilter, setSearchQuery]);

  useEffect(() => {
    localStorage.setItem("page", currentPage);
  }, [currentPage]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      setSort({
        by: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({
        by: "submittedAt",
        order: "desc",
      });
    }
  };

  const Columns = [
    {
      title: trans("Name"),
      dataIndex: "formname",
      width: 200,
      fixed: !isRTL ? "left" : undefined,
      render: (_, record) => (
        <span className={"table-element-name ps-0"}>{record?.form?.name}</span>
      ),
    },
    {
      title: trans("Project Name"),
      dataIndex: "projectName",
      width: 150,
      render: (_, record) => (
        <span>{getFieldValue(record, FIELD_KEYS.projectName)}</span>
      ),
    },
    {
      title: trans("Concerned Department"),
      dataIndex: "concernedDepartment",
      width: 150,
      render: (_, record) => (
        <span>{getFieldValue(record, FIELD_KEYS.concernedDepartment)}</span>
      ),
    },
    {
      title: trans("Central Department"),
      dataIndex: "centralDepartment",
      width: 150,
      render: (_, record) => (
        <span>{getFieldValue(record, FIELD_KEYS.centralDepartment)}</span>
      ),
    },
    {
      title: trans("Status"),
      dataIndex: "status",
      width: 150,
      render: (status) => (
        <Tag color={status !== "submitted" ? "default" : "green"}>
          {status !== "submitted" ? trans("Drafted") : trans("Submitted")}
        </Tag>
      ),
    },
    {
      title: trans("Submitted By"),
      dataIndex: "submittedAt",
      sorter: true,
      width: 150,
      render: (_, record) => (
        <span>
          {record?.submittedBy?.name ? record?.submittedBy?.name : "- - - - -"}
        </span>
      ),
    },
    {
      title: trans("Submitted At"),
      dataIndex: "submittedAt",
      sorter: true,
      width: 150,
      render: (submittedAt) => <span>{formatDateString(submittedAt)}</span>,
    },
    {
      title: trans("updated at"),
      dataIndex: "updatedAt",
      width: 150,
      render: (updatedAt) => (
        <span className="text-capitalize">{formatDateString(updatedAt)}</span>
      ),
    },
    {
      title: trans("Actions"),
      width: 200,
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Button
            text={trans("view")}
            type="default"
            htmlType="button"
            onClick={() => {
              setSubmittedData(record);
              setView(true);
            }}
          />
          <Button
            text={trans("Print")}
            type="default"
            htmlType="button"
            onClick={() => handlePrint(record?.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Helmet>
        <title>Near Miss</title>
        <meta name="description" content="Near Miss" />
      </Helmet>
      <BreadCrumb title={trans("Near Miss")} />
      <div className="bg-white p-16 d-flex align-items-baseline justify-content-between gap-3">
        <Searchbar
          style="search search-dark input-group"
          path={`${process.env.PUBLIC_URL}/assets/Search.svg`}
          placeholder={trans("Search")}
          value={query}
          onChange={handleSearch}
        />
        {canCreateNearMiss && (
          <Button
            text={trans("Add Near Miss")}
            type="primary"
            htmlType="button"
            onClick={() => setAdd(true)}
          />
        )}
      </div>
      <div className="bg-white-rounded-top mt-3 table-parent">
        <AntdTable
          columns={Columns}
          dataSource={AllForms?.data || []}
          loading={isLoading}
          pageSize={limit}
          totalItems={AllForms?.totalResults || 0} // Total count for pagination
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setCurrentPage(1);
          }}
          onTableChange={handleTableChange}
        />
      </div>
      <FormSubmissionPopup setView={setView} view={view} data={submittedData} />
      <NearMissAddPopup
        show={add}
        setShow={setAdd}
        onSuccess={() => {
          setCurrentPage(1);
          refetch();
        }}
      />
    </div>
  );
}